import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Dialog,
} from '@mui/material';
import Select from '@mui/material/Select';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import DialogIconClose from 'component/dialog-icon-close';
import {
    useEditCardMutation,
    useGetCardDataQuery,
} from 'store/slices/cards/card/edit-card';
import { CardUpdateRequest } from 'types/card/card-list';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { useGameCategoryListQuery } from 'store/slices/cards/card/card-list';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useProductTitleSearchListQuery } from 'store/slices/toon-tint/toon-tint';
import ViewProduct from 'component/view-product';

interface Props {
    setOpenEditCardDialog: React.Dispatch<React.SetStateAction<boolean>>;
    cardId: string;
    refetch: any;
    statusList: string[] | undefined;
    cardStatus: string;
}

const exchangePrice = [{ label: 'Gold', value: 'gold' }];

const EditCard: React.FC<Props> = (props: Props) => {
    const { setOpenEditCardDialog, statusList, cardId, refetch, cardStatus } =
        props;

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CardUpdateRequest>({
        defaultValues: {
            cardTitle: undefined,
            positionInCollection: undefined,
            cardId: undefined,
            cardCategory: undefined,
            allowedGamesCard: [],
            exchangePrice: undefined,
            exchangeCurrency: undefined,
        },
    });

    const [searchProduct, setSearchProduct] = useState('');
    const [viewProduct, setViewProduct] = useState('');
    const [openViewProductDialog, setOpenViewProductDialog] =
        React.useState(false);

    const { data: getGameTypeList, isFetching } = useGameCategoryListQuery();

    const { data, isSuccess: dataIsSuccess } = useGetCardDataQuery(cardId, {
        refetchOnMountOrArgChange: true,
    });
    const { data: dataProductCard, isFetching: productCardFatching } =
        useProductTitleSearchListQuery({
            productCategory: 'card',
            productType: 'default',
            searchText: searchProduct,
        });

    const [editCard, { error, isError, isSuccess, isLoading }] =
        useEditCardMutation();

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();

    const onSubmit = (values: CardUpdateRequest) => {
        editCard({
            ...values,
            allowedGamesCard:
                values.allowedGamesCard && values.allowedGamesCard.length > 0
                    ? values.allowedGamesCard
                    : undefined,
            cardId: cardId,
            status: cardStatus,
        });
    };

    const closeModal = () => {
        setOpenEditCardDialog(false);
        reset();
    };

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    useEffect(() => {
        if (isSuccess) {
            setOpenEditCardDialog(false);
            refetch();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (dataIsSuccess) {
            reset({
                cardTitle: data.title,
                positionInCollection: data.positionInCollection ?? 0,
                cardCategory: data.cardCategory ?? '',
                allowedGamesCard: data.allowedGamesCard ?? [],
                exchangeCurrency: data.exchangeCurrency ?? '',
                exchangePrice: data.exchangePrice ?? '',
                productId: data.productId ?? '',
                productTitle: data.productTitle ?? '',
            });
            setViewProduct(data.productId);
        }
    }, [dataIsSuccess, data, reset]);

    return (
        <>
            <Box
                display={'flex'}
                justifyContent={'center'}
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle textTransform={'uppercase'}>EDIT CARD</DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                {dataIsSuccess && (
                    <Box
                        ref={formRef}
                        component="form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <Box display={'flex'} pt={pxToRem(15)}>
                            <Controller
                                name="cardTitle"
                                control={control}
                                rules={{ required: 'Card Title is required' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        sx={{ width: '50%' }}
                                        value={field.value ?? ''}
                                        label="Card Title"
                                        error={!!fieldState.error}
                                        helperText={
                                            fieldState.error?.message || ''
                                        }
                                    />
                                )}
                            />

                            <Controller
                                name="positionInCollection"
                                control={control}
                                rules={{
                                    required:
                                        'Position Collection Title is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10), width: '50%' }}
                                        disabled={cardStatus === 'active'}
                                        name={name}
                                        value={value}
                                        onChange={(e) =>
                                            onChange(
                                                Number(e.target.value) || '',
                                            )
                                        }
                                        label="Position Collection"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Box display={'flex'} pt={pxToRem(15)}>
                            <FormControl fullWidth>
                                <InputLabel disabled={cardStatus === 'active'}>
                                    CardCategory
                                </InputLabel>
                                <Controller
                                    name="cardCategory"
                                    control={control}
                                    defaultValue={data.cardCategory || ''}
                                    render={({
                                        field: { name, value, onChange },
                                    }) => (
                                        <Select
                                            label="CardCategory"
                                            disabled={cardStatus === 'active'}
                                            name={name}
                                            value={
                                                value || data.cardCategory || ''
                                            }
                                            onChange={onChange}
                                            inputProps={{
                                                'aria-label': 'CardCategory',
                                            }}
                                        >
                                            {statusList?.map((status) => (
                                                <MenuItem
                                                    key={status.toString()}
                                                    value={status.toString()}
                                                >
                                                    {status}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Box>

                        <Box display={'flex'} pt={pxToRem(15)}>
                            <FormControl
                                error={Boolean(errors.exchangeCurrency)}
                                fullWidth
                            >
                                <InputLabel>Exchange Currency</InputLabel>
                                <Controller
                                    name="exchangeCurrency"
                                    control={control}
                                    rules={{
                                        required:
                                            'Exchange Currency is required',
                                    }}
                                    render={({
                                        field: { name, value = '', onChange },
                                    }) => (
                                        <Select
                                            label="Exchange Currency"
                                            name={name}
                                            value={value}
                                            onChange={onChange}
                                        >
                                            {exchangePrice?.map(
                                                (status, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={status.value.toString()}
                                                    >
                                                        {status.label}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    )}
                                />
                                {errors.exchangeCurrency && (
                                    <FormHelperText>
                                        {errors.exchangeCurrency.message?.toString()}
                                    </FormHelperText>
                                )}
                            </FormControl>

                            <Controller
                                name="exchangePrice"
                                control={control}
                                rules={{
                                    required: 'Exchange Price is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <TextField
                                        sx={{ ml: pxToRem(10) }}
                                        fullWidth
                                        name={name}
                                        value={value}
                                        onChange={(e) =>
                                            onChange(
                                                Number(e.target.value) || '',
                                            )
                                        }
                                        label="Exchange Price"
                                        error={Boolean(errors[name])}
                                        helperText={
                                            errors[name]?.message?.toString() ||
                                            ''
                                        }
                                    />
                                )}
                            />
                        </Box>

                        <Box
                            pt={pxToRem(15)}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            <Controller
                                name="allowedGamesCard"
                                control={control}
                                render={({
                                    field: { value = [], onChange, ref },
                                }) => {
                                    const backendGames = React.useMemo(() => {
                                        if (
                                            !data?.allowedGamesCard ||
                                            !Array.isArray(
                                                data.allowedGamesCard,
                                            )
                                        )
                                            return [];
                                        return data.allowedGamesCard.map(
                                            (g) => g,
                                        );
                                    }, [data]);

                                    const allOptions = React.useMemo(() => {
                                        const opts = getGameTypeList ?? [];
                                        const missing = backendGames.filter(
                                            (g) => !opts.includes(g),
                                        );
                                        return [...missing, ...opts];
                                    }, [getGameTypeList, backendGames]);

                                    const selectedValues = allOptions.filter(
                                        (opt) => (value ?? []).includes(opt),
                                    );

                                    return (
                                        <Autocomplete
                                            multiple
                                            disableCloseOnSelect
                                            limitTags={5}
                                            sx={{ width: '50%' }}
                                            options={allOptions}
                                            loading={isFetching}
                                            value={selectedValues}
                                            onChange={(_, newValue) => {
                                                onChange(
                                                    newValue.map((v) => v),
                                                );
                                            }}
                                            getOptionLabel={(option) =>
                                                option || ''
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                val,
                                            ) => option === val}
                                            filterSelectedOptions
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    label="Allowed Games Card"
                                                    variant="outlined"
                                                    fullWidth
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFetching && (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />

                            <Controller
                                name="productId"
                                control={control}
                                render={({
                                    field: { onChange, ref, value },
                                }) => {
                                    const backendProduct =
                                        data?.productId && data?.productTitle
                                            ? {
                                                  id: data.productId,
                                                  title: data.productTitle,
                                              }
                                            : null;

                                    const allOptions = React.useMemo(() => {
                                        const opts = dataProductCard ?? [];
                                        if (
                                            backendProduct &&
                                            !opts.some(
                                                (o) =>
                                                    o.id === backendProduct.id,
                                            )
                                        ) {
                                            return [backendProduct, ...opts];
                                        }
                                        return opts;
                                    }, [dataProductCard, backendProduct]);

                                    const selectedValue =
                                        allOptions.find(
                                            (opt) => opt.id === value,
                                        ) ??
                                        backendProduct ??
                                        null;

                                    return (
                                        <Autocomplete
                                            disableCloseOnSelect
                                            sx={{
                                                width: viewProduct
                                                    ? '40%'
                                                    : '50%',
                                                ml: pxToRem(10),
                                            }}
                                            options={allOptions}
                                            loading={productCardFatching}
                                            value={selectedValue}
                                            onChange={(_, newValue) => {
                                                onChange(
                                                    newValue ? newValue.id : '',
                                                );
                                                setViewProduct(
                                                    newValue ? newValue.id : '',
                                                );
                                            }}
                                            onInputChange={(
                                                _,
                                                newInput,
                                                reason,
                                            ) => {
                                                if (reason !== 'reset') {
                                                    setSearchProduct(newInput);
                                                }
                                            }}
                                            getOptionLabel={(option) =>
                                                typeof option === 'string'
                                                    ? option
                                                    : (option.title ?? '')
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                val,
                                            ) => option.id === val.id}
                                            filterSelectedOptions
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    label="Product"
                                                    variant="outlined"
                                                    fullWidth
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {productCardFatching && (
                                                                    <CircularProgress
                                                                        color="inherit"
                                                                        size={
                                                                            20
                                                                        }
                                                                    />
                                                                )}
                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    );
                                }}
                            />

                            {(viewProduct || data.productId) && (
                                <Box
                                    pl={pxToRem(10)}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    <Tooltip title="View Product">
                                        <PageviewIcon
                                            fontSize={'large'}
                                            onClick={() => {
                                                setOpenViewProductDialog(true);
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Edit Card
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <Dialog
                open={openViewProductDialog}
                onClose={() => setOpenViewProductDialog(false)}
                slotProps={{
                    paper: {
                        sx: {
                            width: '800px',
                            minHeight: '600px',
                            maxWidth: '90%',
                        },
                    },
                }}
            >
                <ViewProduct
                    setOpenViewProductDialog={setOpenViewProductDialog}
                    viewProduct={viewProduct}
                />
            </Dialog>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Edit an Card!'}
            />
        </>
    );
};
export default EditCard;
